import {
  computeRingCentroid,
  parseBoundaryGeoJsonRing,
  pointInPolygon,
} from "./mapMeasurements";

const GAUGE_LABELS = ["Noroeste", "Nordeste", "Sudeste", "Sudoeste", "Centro"];

const DEFAULT_DEMO_MM = [8, 22, 48, 16, 32];

export function findSanRafaelSubFarm(groupFarms) {
  return (groupFarms || []).find((farm) => {
    if (!farm || !farm.parent) return false;
    const name = (farm.nome_da_fazenda || "").toLowerCase();
    return name.includes("san rafael");
  });
}

export function distanceMeters(a, b) {
  if (!a || !b) return 0;
  const earthRadius = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

function clampPointInsideRing(point, ring, centroid) {
  if (pointInPolygon(point, ring)) {
    return point;
  }
  if (!centroid) return point;
  return {
    lat: point.lat * 0.72 + centroid.lat * 0.28,
    lng: point.lng * 0.72 + centroid.lng * 0.28,
  };
}

export function buildGaugePositionsFromRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return [];
  }

  const centroid = computeRingCentroid(ring);
  const lats = ring.map((point) => point.lat);
  const lngs = ring.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const insetLat = (maxLat - minLat) * 0.12;
  const insetLng = (maxLng - minLng) * 0.12;

  const rawPoints = [
    { lat: maxLat - insetLat, lng: minLng + insetLng },
    { lat: maxLat - insetLat, lng: maxLng - insetLng },
    { lat: minLat + insetLat, lng: maxLng - insetLng },
    { lat: minLat + insetLat, lng: minLng + insetLng },
    centroid,
  ].filter(Boolean);

  return rawPoints.map((point, index) => {
    const position = clampPointInsideRing(point, ring, centroid);
    return {
      id: `gauge-${index + 1}`,
      label: GAUGE_LABELS[index] || `Ponto ${index + 1}`,
      lat: position.lat,
      lng: position.lng,
      mm: DEFAULT_DEMO_MM[index] || 0,
    };
  });
}

export function interpolateRainMmAtPoint(point, gauges, power = 2) {
  if (!point || !Array.isArray(gauges) || gauges.length === 0) {
    return 0;
  }

  let numerator = 0;
  let denominator = 0;

  gauges.forEach((gauge) => {
    const distance = Math.max(distanceMeters(point, gauge), 8);
    const weight = 1 / distance ** power;
    numerator += weight * (Number(gauge.mm) || 0);
    denominator += weight;
  });

  return denominator > 0 ? numerator / denominator : 0;
}

export function buildHeatmapWeightedPoints(ring, gauges, gridSteps = 20) {
  const effectiveRing = resolveHeatmapRing(ring, gauges);
  if (!Array.isArray(effectiveRing) || effectiveRing.length < 3 || !Array.isArray(gauges)) {
    return [];
  }

  const lats = effectiveRing.map((point) => point.lat);
  const lngs = effectiveRing.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latStep = (maxLat - minLat) / gridSteps;
  const lngStep = (maxLng - minLng) / gridSteps;
  const points = [];
  const seen = new Set();

  function pushPoint(point, mm, boost = 1) {
    const weight = Math.max(Number(mm) || 0, 0);
    if (weight <= 0) return;
    const key = `${point.lat.toFixed(6)}:${point.lng.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({
      lat: point.lat,
      lng: point.lng,
      mm: weight,
      weight: weight * boost,
    });
  }

  (gauges || []).forEach((gauge) => {
    pushPoint(
      { lat: gauge.lat, lng: gauge.lng },
      gauge.mm,
      2.5
    );
  });

  for (let row = 0; row <= gridSteps; row += 1) {
    for (let col = 0; col <= gridSteps; col += 1) {
      const sample = {
        lat: minLat + latStep * row,
        lng: minLng + lngStep * col,
      };
      if (!pointInPolygon(sample, effectiveRing)) {
        continue;
      }
      const mm = interpolateRainMmAtPoint(sample, gauges);
      pushPoint(sample, mm, 1);
    }
  }

  return points;
}

export function getHeatmapMaxIntensity(points, gauges) {
  const values = [
    ...(points || []).map(
      (point) => Number(point.weight != null ? point.weight : point.mm) || 0
    ),
    ...(gauges || []).map((gauge) => (Number(gauge.mm) || 0) * 2.5),
  ].filter((value) => value > 0);

  if (!values.length) return 1;
  return Math.max(...values, 1);
}

export function getHeatmapGradient() {
  return [
    "rgba(33, 150, 243, 0)",
    "rgba(33, 150, 243, 0.45)",
    "rgba(0, 188, 212, 0.6)",
    "rgba(76, 175, 80, 0.75)",
    "rgba(255, 235, 59, 0.88)",
    "rgba(255, 152, 0, 0.95)",
    "rgba(244, 67, 54, 1)",
  ];
}

export function formatRainMm(value) {
  const mm = Number(value);
  if (!Number.isFinite(mm)) return "0";
  return mm.toFixed(1).replace(".0", "");
}

export function sumGaugeRainMm(gauges) {
  return (gauges || []).reduce(
    (total, gauge) => total + (Number(gauge.mm) || 0),
    0
  );
}

export function averageGaugeRainMm(gauges) {
  const list = gauges || [];
  if (!list.length) return 0;
  return sumGaugeRainMm(list) / list.length;
}

export function buildGaugesFromStations(stations) {
  return (stations || []).map((station) => ({
    id: station.stationUuid,
    stationUuid: station.stationUuid,
    readingUuid: station.readingUuid,
    label: station.stationName || "Pluviômetro",
    stationNumber: station.stationNumber || "",
    lat: Number(station.lat),
    lng: Number(station.lng),
    mm: Number(station.mm) || 0,
    periodStart: station.periodStart,
    periodEnd: station.periodEnd,
    recordedAt: station.recordedAt,
  }));
}

export function buildHeatmapBoundsFromGauges(gauges, padding = 0.01) {
  if (!gauges || !gauges.length) return [];
  const lats = gauges.map((gauge) => gauge.lat);
  const lngs = gauges.map((gauge) => gauge.lng);
  const minLat = Math.min(...lats) - padding;
  const maxLat = Math.max(...lats) + padding;
  const minLng = Math.min(...lngs) - padding;
  const maxLng = Math.max(...lngs) + padding;
  return [
    { lat: minLat, lng: minLng },
    { lat: minLat, lng: maxLng },
    { lat: maxLat, lng: maxLng },
    { lat: maxLat, lng: minLng },
  ];
}

export function resolveHeatmapRing(boundaryRing, gauges) {
  if (Array.isArray(boundaryRing) && boundaryRing.length >= 3) {
    return boundaryRing;
  }
  return buildHeatmapBoundsFromGauges(gauges);
}

export function resolvePrimaryBoundaryRing(groupFarms, contextFarmId) {
  const farms = groupFarms || [];
  const parent = farms.find(
    (farm) => String(farm.id) === String(contextFarmId)
  );
  if (parent && parent.boundary_geojson) {
    const ring = parseBoundaryGeoJsonRing(parent.boundary_geojson);
    if (ring.length >= 3) return ring;
  }

  let largestRing = [];
  farms.forEach((farm) => {
    const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
    if (ring.length > largestRing.length) {
      largestRing = ring;
    }
  });
  return largestRing;
}

export function toDateInputValue(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDateInputBr(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function formatRainPeriodLabel(period, customStart, customEnd, translate) {
  const t = translate || ((key) => key);
  if (period === "custom") {
    if (customStart && customEnd && customStart === customEnd) {
      return formatDateInputBr(customStart);
    }
    if (customStart || customEnd) {
      return `${formatDateInputBr(customStart)} – ${formatDateInputBr(customEnd)}`;
    }
    return t("Personalizado");
  }
  const labels = {
    today: t("Hoje"),
    week: t("Última semana"),
    month: t("Último mês"),
  };
  return labels[period] || t("Hoje");
}

export function resolveQuickRainDate(period, customStart, customEnd) {
  if (period === "today") return toDateInputValue();
  if (period === "custom" && customStart && customEnd && customStart === customEnd) {
    return customStart;
  }
  return null;
}

export const DEFAULT_RAIN_MAP_ZOOM = 16;
export const RAIN_MAP_FIT_MAX_SPAN_DEG = 0.25;

export function shouldUseCloseRainZoom(points) {
  const list = (points || []).filter(
    (point) =>
      point &&
      Number.isFinite(Number(point.lat)) &&
      Number.isFinite(Number(point.lng))
  );
  if (list.length < 2) return true;
  const lats = list.map((point) => Number(point.lat));
  const lngs = list.map((point) => Number(point.lng));
  return (
    Math.max(...lats) - Math.min(...lats) > RAIN_MAP_FIT_MAX_SPAN_DEG ||
    Math.max(...lngs) - Math.min(...lngs) > RAIN_MAP_FIT_MAX_SPAN_DEG
  );
}

export function computeRainMapCenter(boundaryRing, gauges) {
  if (gauges && gauges.length) {
    const lat =
      gauges.reduce((sum, gauge) => sum + gauge.lat, 0) / gauges.length;
    const lng =
      gauges.reduce((sum, gauge) => sum + gauge.lng, 0) / gauges.length;
    return { center: { lat, lng }, zoom: DEFAULT_RAIN_MAP_ZOOM };
  }

  const centroid = computeRingCentroid(boundaryRing);
  if (!centroid) {
    return { center: null, zoom: DEFAULT_RAIN_MAP_ZOOM };
  }
  return { center: centroid, zoom: DEFAULT_RAIN_MAP_ZOOM };
}
